/**
 * Application chrome for pages that are not the three-pane calculation view.
 *
 * The workspace switcher appears only when the user belongs to more than one
 * workspace. That is ADR-0002's central UX promise: "a solo engineer never sees the
 * word *workspace*: they log in, they have projects, done." A switcher showing one
 * entry is exactly the complexity the personal-workspace model exists to hide.
 */
import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { FileSpreadsheet, FolderTree, LifeBuoy, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { UserMenu } from '@/components/layout/UserMenu'
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher'

export function WorkspaceChrome({
  title,
  children,
  actions,
}: {
  title: string
  children: ReactNode
  actions?: ReactNode
}) {
  const { t } = useTranslation()

  const navClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex h-11 items-center gap-2 rounded-md px-3 text-sm',
      isActive ? 'bg-surface-raised text-text' : 'text-text-muted hover:text-text',
    )

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface-raised focus:px-3 focus:py-2"
      >
        {t('nav.skip_to_content')}
      </a>

      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-3 sm:px-4">
        <Link to="/" className="font-semibold">
          {t('app.name')}
        </Link>

        <WorkspaceSwitcher />

        <nav className="ms-2 hidden items-center gap-1 sm:flex" aria-label={t('nav.dashboard')}>
          <NavLink to="/projects" className={navClass}>
            <FolderTree size={15} aria-hidden />
            {t('nav.projects')}
          </NavLink>
          <NavLink to="/sheets" className={navClass}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
            {t('nav.sheets')}
          </NavLink>
          <NavLink to="/support" className={navClass}>
            <LifeBuoy size={15} aria-hidden />
            {t('nav.support')}
          </NavLink>
          <NavLink to="/settings" className={navClass}>
            <Settings size={15} aria-hidden />
            {t('nav.settings')}
          </NavLink>
        </nav>

        <div className="ms-auto flex items-center gap-1">
          {actions}
          <LanguageToggle />
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <main id="main" className="min-h-0 flex-1">
        <h1 className="sr-only">{title}</h1>
        {children}
      </main>

      {/* Below `sm` the header nav is hidden, so navigation lives here instead --
          otherwise a phone user can reach projects and nothing else (14.3). */}
      <nav
        className="sticky bottom-0 z-30 flex shrink-0 border-t border-border bg-surface sm:hidden"
        aria-label={t('nav.dashboard')}
      >
        {[
          { to: '/projects', icon: FolderTree, labelKey: 'nav.projects' },
          { to: '/sheets', icon: FileSpreadsheet, labelKey: 'nav.sheets' },
          { to: '/support', icon: LifeBuoy, labelKey: 'nav.support' },
          { to: '/settings', icon: Settings, labelKey: 'nav.settings' },
        ].map(({ to, icon: Icon, labelKey }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-xs',
                'min-h-[3.25rem] pb-[max(0.5rem,env(safe-area-inset-bottom))]',
                isActive ? 'text-accent' : 'text-text-muted',
              )
            }
          >
            <Icon size={18} aria-hidden />
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
